<!--
  ContactField — the grid renderer for contact fields: a schema field typed
  email / phone / url / location, the generic contact field, and the per-kind
  aggregate columns (Emails, Phones, Socials, Locations).

  In the row, one line per contact, the value in link blue: a short row shows
  the first and a count, a medium row up to three lines, a tall row up to four
  with the label and primary flag under each. A field of one kind leaves the
  icon to its column header until the cell is selected; a field of any kind,
  and socials (whose icon is the platform), always lead with it. At rest a
  value links to mail, call, the page or the map; selected, a click edits.

  Editing opens ContactsListEditor: click a contact to change it in place, × to
  remove, type in the last line to add. It sends nothing until Done, which
  hands over an `{ add, remove, update }` delta naming only what changed, so
  clicking away discards the edit. A changed value is an update of its row, so
  its label, primary flag and position survive.
-->
<template>
  <div class="contact-field" :class="{ 'is-editing': editing }">
    <ContactsListEditor
      v-if="editing"
      :rows="rows"
      :field="field"
      :seed="seed"
      @click.stop
      @done="setMany"
      @cancel="emit('cancel')"
    />

    <template v-else>
      <ContactElement
        v-for="row in shown"
        :key="row.id"
        :value="row"
        :show-kind="kindShown"
        :fixed="mode !== 'inline'"
        :detail="rowHeight === 'tall'"
      />
      <span v-if="more" class="more">+{{ more }}</span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from "vue"
import { isRelationDelta } from "@auger/core/collections"
import { type Field, referenceEntities } from "@auger/core/fields"
import ContactElement from "@auger/ui/components/elements/ContactElement.vue"
import { GRID_ROWS_CLASS } from "@auger/ui/composables/useGrid"
import { CELL_CONTEXT } from "@auger/ui/composables/useInteraction"
import { type FieldMode, type FieldSize } from "@auger/ui/fields/useField"
import type { ContactRow } from "@auger/ui/utils/contacts"
import ContactsListEditor from "@/components/contacts/ContactsListEditor.vue"
import { contactKindOf, type ContactsDelta } from "@/composables/useContactsEditor"

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: Field
    mode: FieldMode
    size?: FieldSize
  }>(),
  { size: "small" }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
}>()

const cell = inject(CELL_CONTEXT, null)
const rowsClass = inject(GRID_ROWS_CLASS, null)

const toRows = (value: unknown): ContactRow[] =>
  (value == null ? [] : Array.isArray(value) ? value : [value]).filter(
    (c): c is ContactRow => !!c && typeof c === "object" && "value" in c
  )

// The field's rows as stored. Outside an edit the value is those rows; while
// editing it is this cell's draft (or a typed key), so read them off the row.
const rows = computed<ContactRow[]>(() => {
  const v = props.modelValue
  if (!isRelationDelta(v) && typeof v !== "string") return toRows(v)
  const item = cell?.item()
  return item ? toRows(referenceEntities(props.field, item)) : []
})

const seed = computed(() => (typeof props.modelValue === "string" ? props.modelValue : ""))

const writable = computed(() => !!props.field.isEditable && !!cell?.writer())
const editing = computed(() => props.mode === "editing" && writable.value)

/** Each line's icon: always where kinds mix (and for socials, whose icon is
 *  the platform); for one kind, once selected. */
const kindShown = computed(
  () =>
    contactKindOf(props.field) == null || props.field.type === "socials" || props.mode === "active"
)

// --- In the row -----------------------------------------------------------------

/** A grid row's height preset, or medium outside a grid (a card). */
const rowHeight = computed(() => {
  const c = rowsClass?.value ?? "is-medium-rows"
  return c === "is-short-rows" ? "short" : c === "is-tall-rows" ? "tall" : "medium"
})

// Room for this many lines; when there are more, one line goes to the count.
const room = computed(() => ({ short: 1, medium: 3, tall: 4 })[rowHeight.value])
const shown = computed(() => {
  const all = rows.value
  if (all.length <= room.value) return all
  return all.slice(0, Math.max(1, room.value - (rowHeight.value === "short" ? 0 : 1)))
})
const more = computed(() => rows.value.length - shown.value.length)

// --- Editing --------------------------------------------------------------------

/** Done: the editor's delta is the draft, committed at once. */
function setMany(delta: ContactsDelta) {
  emit("update:modelValue", delta)
  emit("commit")
}
</script>

<style>
.contact-field {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  min-width: 0;
  /* The size every other cell's value reads at. */
  font-size: var(--auger-size-7);

  /* How many more contacts than the row has room for: a small grey tag. */
  .more {
    flex: none;
    padding: 1px 6px;
    border-radius: var(--auger-radius);
    background: var(--auger-bg-muted);
    color: var(--auger-text-weak);
    font-size: 0.9em;
    font-weight: 500;
    line-height: 1.4;
  }
}

.contact-field:not(.is-editing) .contact-line {
  min-height: 20px;
}

/* A short row is one line: the first contact and the count, pulled right. */
.is-short-rows .contact-field:not(.is-editing) {
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 6px;

  .contact-element {
    flex: 0 1 auto;
  }

  .more {
    margin-left: auto;
  }
}

.is-tall-rows .contact-field:not(.is-editing) {
  justify-content: flex-start;
}

/* Editing, the editor fills the overlay, which widens to fit it. */
.contact-field.is-editing {
  align-items: stretch;
}

.grid-editor:has(> .contact-field.is-editing) {
  width: min-content;
  max-width: 480px;
  position-try-fallbacks:
    flip-inline,
    flip-block,
    flip-block flip-inline;
}

.grid-editor > .contact-field.is-editing {
  min-width: 360px;
}
</style>
